import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocalHospital,
  Medication,
  Receipt,
  Description,
  Sync,
  Refresh,
  Download,
  HelpOutline,
} from '@mui/icons-material';
import api from '../utils/api';
import { useAppSelector } from '../store/hooks';
import GradientDialogTitle from '../components/GradientDialogTitle';

interface ELGAStatus {
  configured: boolean;
  environment: string;
  hasCertificates: boolean;
  errors: string[];
}

interface ELGAPatient {
  _id?: string;
  id?: string;
  firstName: string;
  lastName: string;
  ecard?: {
    elgaId?: string;
    elgaStatus?: string;
  };
}

interface MedicationItem {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  prescriber: string;
}

interface Prescription {
  id: string;
  medication: string;
  dosage: string;
  quantity: number;
  date: string;
  prescriber: string;
  status: string;
}

interface ELGADocument {
  id: string;
  title: string;
  classCode: string;
  typeCode: string;
  date: string;
  author: string;
}

const ELGA: React.FC = () => {
  const { patients } = useAppSelector((state) => state.patients);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [elgaStatus, setElgaStatus] = useState<ELGAStatus | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<ELGAPatient | null>(null);
  const [medications, setMedications] = useState<MedicationItem[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [documents, setDocuments] = useState<ELGADocument[]>([]);
  const [patientStatus, setPatientStatus] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpTab, setHelpTab] = useState(0);

  useEffect(() => {
    fetchELGAStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchELGAStatus bewusst ausgelassen
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchPatientData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPatientData bewusst ausgelassen
  }, [selectedPatient, activeTab]);

  const fetchELGAStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get<any>('/elga/status');
      if (response.success && response.data) {
        // Stelle sicher, dass errors immer ein Array ist
        const statusData = {
          ...response.data,
          errors: Array.isArray(response.data.errors) ? response.data.errors : []
        };
        setElgaStatus(statusData);
      }
    } catch (error: any) {
      console.error('Error fetching ELGA status:', error);
      showSnackbar('Fehler beim Laden des ELGA-Status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientData = async () => {
    if (!selectedPatient) return;

    try {
      setLoading(true);

      if (activeTab === 0) {
        // e-Medikation
        const response = await api.get<{ medications: MedicationItem[] }>(
          `/elga/patient/${selectedPatient._id || selectedPatient.id}/medication`
        );
        if (response.success && response.data) {
          setMedications(response.data.medications || []);
        }
      } else if (activeTab === 1) {
        // e-Rezepte
        const response = await api.get<{ prescriptions: Prescription[] }>(
          `/elga/patient/${selectedPatient._id || selectedPatient.id}/prescriptions`
        );
        if (response.success && response.data) {
          setPrescriptions(response.data.prescriptions || []);
        }
      } else if (activeTab === 2) {
        // e-Befunde/Dokumente
        const response = await api.get<{ documents: ELGADocument[] }>(
          `/elga/patient/${selectedPatient._id || selectedPatient.id}/documents`
        );
        if (response.success && response.data) {
          setDocuments(response.data.documents || []);
        }
      }

      // Patient-Status abrufen
      const statusResponse = await api.get<any>(
        `/elga/patient/${selectedPatient._id || selectedPatient.id}/status`
      );
      if (statusResponse.success && statusResponse.data) {
        setPatientStatus(statusResponse.data);
      }
    } catch (error: any) {
      console.error('Error fetching patient data:', error);
      // Wenn Patient nicht in ELGA registriert ist, ist das kein kritischer Fehler
      const errorMessage = error?.message || '';
      if (errorMessage.includes('nicht in ELGA registriert')) {
        setMedications([]);
        setPrescriptions([]);
        setDocuments([]);
        showSnackbar('Patient ist nicht in ELGA registriert', 'info');
      } else {
        showSnackbar('Fehler beim Laden der Patientendaten', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!selectedPatient) return;

    try {
      setLoading(true);
      const response = await api.post<{ success: boolean; message: string }>(
        `/elga/patient/${selectedPatient._id || selectedPatient.id}/sync`
      );
      if (response.success) {
        showSnackbar('Patientendaten erfolgreich synchronisiert', 'success');
        await fetchPatientData();
        setSyncDialogOpen(false);
      }
    } catch (error: any) {
      console.error('Error syncing patient:', error);
      showSnackbar('Fehler bei der Synchronisierung', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              ELGA Integration
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Elektronische Gesundheitsakte und e-Medikation
            </Typography>
          </Box>
          <Tooltip title="Hilfe & Leitfaden">
            <IconButton
              onClick={() => setHelpDialogOpen(true)}
              color="primary"
            >
              <HelpOutline />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchELGAStatus}
          disabled={loading}
        >
          Status aktualisieren
        </Button>
      </Box>

      {/* ELGA Status */}
      {elgaStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalHospital sx={{ mr: 1, color: elgaStatus.configured ? 'success.main' : 'error.main' }} />
              <Typography variant="h6">ELGA-Systemstatus</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Konfiguriert
                </Typography>
                <Chip
                  label={elgaStatus.configured ? 'Ja' : 'Nein'}
                  color={elgaStatus.configured ? 'success' : 'error'}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Umgebung
                </Typography>
                <Typography variant="body1">{elgaStatus.environment}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Zertifikate
                </Typography>
                <Chip
                  label={elgaStatus.hasCertificates ? 'Vorhanden' : 'Fehlen'}
                  color={elgaStatus.hasCertificates ? 'success' : 'warning'}
                  size="small"
                />
              </Grid>
              {elgaStatus.errors && Array.isArray(elgaStatus.errors) && elgaStatus.errors.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">
                    <Typography variant="subtitle2" gutterBottom>
                      Konfigurationsfehler:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {elgaStatus.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Patient-Auswahl */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Patient auswählen
          </Typography>
          <Autocomplete
            options={patients || []}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => 
              (option._id || option.id) === (value._id || value.id)
            }
            value={selectedPatient as any}
            onChange={(_, newValue) => setSelectedPatient(newValue as ELGAPatient | null)}
            renderInput={(params) => (
              <TextField {...params} label="Patient" placeholder="Patient suchen..." />
            )}
            renderOption={(props, option) => {
              const { key: _key, ...otherProps } = props;
              const uniqueKey = option._id || option.id || `${option.firstName}-${option.lastName}-${option.dateOfBirth || Math.random()}`;
              return (
                <Box component="li" key={uniqueKey} {...otherProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography>{`${option.firstName} ${option.lastName}`}</Typography>
                      {option.ecard?.elgaId && (
                        <Chip
                          label={`ELGA-ID: ${option.ecard.elgaId}`}
                          size="small"
                          color="primary"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            }}
          />
          {selectedPatient && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Sync />}
                onClick={() => setSyncDialogOpen(true)}
                disabled={loading}
              >
                Mit ELGA synchronisieren
              </Button>
              {patientStatus && (
                <Chip
                  label={`Status: ${patientStatus.status || 'Unbekannt'}`}
                  color={patientStatus.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tabs für e-Medikation, e-Rezepte, e-Befunde */}
      {selectedPatient && (
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab icon={<Medication />} iconPosition="start" label="e-Medikation" />
              <Tab icon={<Receipt />} iconPosition="start" label="e-Rezepte" />
              <Tab icon={<Description />} iconPosition="start" label="e-Befunde" />
            </Tabs>
          </Box>

          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      e-Medikation
                    </Typography>
                    {medications.length === 0 ? (
                      <Alert severity="info">Keine Medikamente gefunden</Alert>
                    ) : (
                      <List>
                        {medications.map((med, index) => (
                          <React.Fragment key={med.id}>
                            <ListItem>
                              <ListItemIcon>
                                <Medication color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={med.name}
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2">
                                      {med.dosage} • {med.frequency}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      {med.startDate} {med.endDate && `- ${med.endDate}`}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      Verschrieben von: {med.prescriber}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                            {index < medications.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      e-Rezepte
                    </Typography>
                    {prescriptions.length === 0 ? (
                      <Alert severity="info">Keine Rezepte gefunden</Alert>
                    ) : (
                      <List>
                        {prescriptions.map((prescription, index) => (
                          <React.Fragment key={prescription.id}>
                            <ListItem>
                              <ListItemIcon>
                                <Receipt color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={prescription.medication}
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2">
                                      {prescription.dosage} • Menge: {prescription.quantity}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      {prescription.date} • {prescription.prescriber}
                                    </Typography>
                                    <br />
                                    <Chip
                                      label={prescription.status}
                                      size="small"
                                      color={prescription.status === 'active' ? 'success' : 'default'}
                                      sx={{ mt: 0.5 }}
                                    />
                                  </>
                                }
                              />
                            </ListItem>
                            {index < prescriptions.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">e-Befunde</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={fetchPatientData}
                        disabled={loading}
                      >
                        Aktualisieren
                      </Button>
                    </Box>
                    {documents.length === 0 ? (
                      <Alert severity="info">Keine Dokumente gefunden</Alert>
                    ) : (
                      <List>
                        {documents.map((doc, index) => (
                          <React.Fragment key={doc.id}>
                            <ListItem>
                              <ListItemIcon>
                                <Description color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={doc.title}
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2">
                                      {doc.classCode} • {doc.typeCode}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="caption" color="text.secondary">
                                      {doc.date} • {doc.author}
                                    </Typography>
                                  </>
                                }
                              />
                              <Tooltip title="Dokument anzeigen">
                                <IconButton
                                  onClick={() => {
                                    // TODO: Implement document viewer
                                    showSnackbar('Dokument-Viewer wird implementiert', 'info');
                                  }}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            </ListItem>
                            {index < documents.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Synchronisierungs-Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <DialogTitle>Mit ELGA synchronisieren</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Patientendaten von {selectedPatient?.firstName} {selectedPatient?.lastName} mit ELGA synchronisieren?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSync} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Synchronisieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hilfe-Dialog mit Leitfaden */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <GradientDialogTitle 
          title="Leitfaden: ELGA Integration" 
          onClose={() => setHelpDialogOpen(false)}
        />
        <DialogContent>
          <Tabs value={helpTab} onChange={(_, v) => setHelpTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Übersicht" />
            <Tab label="e-Medikation" />
            <Tab label="e-Rezepte" />
            <Tab label="e-Befunde" />
            <Tab label="Synchronisation" />
          </Tabs>

          {helpTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Was ist ELGA?
                </Typography>
                <Typography variant="body1" paragraph>
                  ELGA (Elektronische Gesundheitsakte) ist das österreichische System für die elektronische 
                  Gesundheitsakte. Es ermöglicht den sicheren Austausch von Gesundheitsdaten zwischen 
                  verschiedenen Gesundheitseinrichtungen.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  ELGA-Systemstatus
                </Typography>
                <Typography variant="body2" paragraph>
                  Der Systemstatus zeigt Ihnen die aktuelle Konfiguration und den Verbindungsstatus:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li><strong>Konfiguriert:</strong> Zeigt an, ob ELGA korrekt konfiguriert ist</li>
                  <li><strong>Umgebung:</strong> Test- oder Produktionsumgebung</li>
                  <li><strong>Zertifikate:</strong> Status der ELGA-Zertifikate</li>
                  <li><strong>Fehler:</strong> Zeigt Konfigurationsfehler an, falls vorhanden</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Hauptfunktionen
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li>📋 <strong>e-Medikation:</strong> Anzeige aller Medikamente aus ELGA</li>
                  <li>💊 <strong>e-Rezepte:</strong> Anzeige aller elektronischen Rezepte</li>
                  <li>📄 <strong>e-Befunde/Dokumente:</strong> Anzeige aller medizinischen Dokumente</li>
                  <li>🔄 <strong>Synchronisation:</strong> Manuelle Synchronisation mit ELGA</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Voraussetzungen
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li>✅ Patient muss in ELGA registriert sein (ELGA-ID vorhanden)</li>
                  <li>✅ ELGA-System muss korrekt konfiguriert sein</li>
                  <li>✅ Gültige ELGA-Zertifikate müssen vorhanden sein</li>
                  <li>✅ Internetverbindung für ELGA-API-Zugriff</li>
                </Box>
              </Box>
            </Box>
          )}

          {helpTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  e-Medikation
                </Typography>
                <Typography variant="body2" paragraph>
                  Die e-Medikation zeigt alle Medikamente an, die für den ausgewählten Patienten in ELGA gespeichert sind.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Angezeigte Informationen
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li><strong>Medikamentenname:</strong> Name des Medikaments</li>
                  <li><strong>Dosierung:</strong> Menge und Einheit (z.B. "10mg")</li>
                  <li><strong>Häufigkeit:</strong> Einnahmehäufigkeit (z.B. "2x täglich")</li>
                  <li><strong>Startdatum:</strong> Beginn der Medikation</li>
                  <li><strong>Enddatum:</strong> Ende der Medikation (falls vorhanden)</li>
                  <li><strong>Verschreiber:</strong> Name des verschreibenden Arztes</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  So verwenden Sie e-Medikation
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                  <li>Wählen Sie einen Patienten aus (Patient muss ELGA-ID haben)</li>
                  <li>Klicken Sie auf den Tab "e-Medikation"</li>
                  <li>Die Medikamente werden automatisch geladen</li>
                  <li>Bei Bedarf: Klicken Sie auf "Mit ELGA synchronisieren" für aktuelle Daten</li>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Hinweis:</strong> Nur Patienten mit ELGA-ID können e-Medikation-Daten anzeigen. 
                  Wenn ein Patient nicht in ELGA registriert ist, wird eine entsprechende Meldung angezeigt.
                </Typography>
              </Alert>
            </Box>
          )}

          {helpTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  e-Rezepte
                </Typography>
                <Typography variant="body2" paragraph>
                  Die e-Rezepte zeigen alle elektronischen Rezepte an, die für den ausgewählten Patienten in ELGA gespeichert sind.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Angezeigte Informationen
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li><strong>Medikament:</strong> Name des verschriebenen Medikaments</li>
                  <li><strong>Dosierung:</strong> Verschriebene Dosierung</li>
                  <li><strong>Menge:</strong> Verschriebene Menge</li>
                  <li><strong>Datum:</strong> Ausstellungsdatum des Rezepts</li>
                  <li><strong>Verschreiber:</strong> Name des verschreibenden Arztes</li>
                  <li><strong>Status:</strong> Status des Rezepts (z.B. "aktiv", "eingelöst")</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  So verwenden Sie e-Rezepte
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                  <li>Wählen Sie einen Patienten aus (Patient muss ELGA-ID haben)</li>
                  <li>Klicken Sie auf den Tab "e-Rezepte"</li>
                  <li>Die Rezepte werden automatisch geladen</li>
                  <li>Bei Bedarf: Klicken Sie auf "Mit ELGA synchronisieren" für aktuelle Daten</li>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Hinweis:</strong> e-Rezepte werden automatisch in ELGA gespeichert, wenn Sie Rezepte 
                  in MyMediCloud MMC erstellen (sofern ELGA-Integration aktiviert ist).
                </Typography>
              </Alert>
            </Box>
          )}

          {helpTab === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  e-Befunde/Dokumente
                </Typography>
                <Typography variant="body2" paragraph>
                  Die e-Befunde/Dokumente zeigen alle medizinischen Dokumente an, die für den ausgewählten Patienten in ELGA gespeichert sind.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Angezeigte Informationen
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li><strong>Titel:</strong> Titel des Dokuments</li>
                  <li><strong>Class Code:</strong> Dokumentenklasse (z.B. "CDA", "Befund")</li>
                  <li><strong>Type Code:</strong> Dokumententyp (z.B. "11534-6" für Progress Note)</li>
                  <li><strong>Datum:</strong> Erstellungsdatum des Dokuments</li>
                  <li><strong>Autor:</strong> Name des erstellenden Arztes</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  So verwenden Sie e-Befunde/Dokumente
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                  <li>Wählen Sie einen Patienten aus (Patient muss ELGA-ID haben)</li>
                  <li>Klicken Sie auf den Tab "e-Befunde"</li>
                  <li>Die Dokumente werden automatisch geladen</li>
                  <li>Klicken Sie auf ein Dokument, um Details anzuzeigen</li>
                  <li>Bei Bedarf: Klicken Sie auf "Mit ELGA synchronisieren" für aktuelle Daten</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Dokumente zu ELGA hochladen
                </Typography>
                <Typography variant="body2" paragraph>
                  Sie können Dokumente (z.B. Dekurs-Einträge) direkt zu ELGA hochladen:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                  <li>Erstellen Sie ein Dokument in MyMediCloud MMC</li>
                  <li>Klicken Sie auf "Zu ELGA senden" (falls verfügbar)</li>
                  <li>Das Dokument wird automatisch in ELGA gespeichert</li>
                  <li>Der Patient kann das Dokument dann in seiner ELGA-Akte sehen</li>
                </Box>
              </Box>

              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Tipp:</strong> Dekurs-Einträge können automatisch als CDA-Dokumente zu ELGA gesendet werden, 
                  wenn Sie die entsprechende Funktion verwenden.
                </Typography>
              </Alert>
            </Box>
          )}

          {helpTab === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Synchronisation mit ELGA
                </Typography>
                <Typography variant="body2" paragraph>
                  Die Synchronisation aktualisiert die Patientendaten aus ELGA und stellt sicher, 
                  dass Sie die aktuellsten Informationen haben.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  So synchronisieren Sie
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                  <li>Wählen Sie einen Patienten aus</li>
                  <li>Klicken Sie auf "Mit ELGA synchronisieren"</li>
                  <li>Bestätigen Sie die Synchronisation im Dialog</li>
                  <li>Das System lädt die aktuellen Daten von ELGA</li>
                  <li>Die Tabs (e-Medikation, e-Rezepte, e-Befunde) werden automatisch aktualisiert</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Was wird synchronisiert?
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li>✅ Aktuelle Medikamente (e-Medikation)</li>
                  <li>✅ Aktuelle Rezepte (e-Rezepte)</li>
                  <li>✅ Neue Dokumente (e-Befunde)</li>
                  <li>✅ ELGA-Status des Patienten</li>
                  <li>✅ Versicherungsdaten (falls aktualisiert)</li>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Automatische Synchronisation
                </Typography>
                <Typography variant="body2" paragraph>
                  Das System synchronisiert automatisch:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <li>Bei der ersten Auswahl eines Patienten</li>
                  <li>Nach dem Wechsel zwischen Tabs (e-Medikation, e-Rezepte, e-Befunde)</li>
                  <li>Nach dem Hochladen eines Dokuments zu ELGA</li>
                </Box>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Wichtig:</strong> Die Synchronisation kann einige Sekunden dauern, 
                  abhängig von der Anzahl der Daten und der Verbindungsgeschwindigkeit.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ELGA;
